import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, QrCode, MapPin } from 'lucide-react';
import LanguageSelector from './LanguageSelector';
import { getStoredLanguage, normalizeLanguage } from '../utils/language';

const Header = ({ onSearch, onQRScan }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(getStoredLanguage());

  useEffect(() => {
    setCurrentLanguage(getStoredLanguage());
    const handler = (e) => {
      const next = normalizeLanguage(e?.detail?.language);
      setCurrentLanguage(next);
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('app-language-change', handler);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('app-language-change', handler);
      }
    };
  }, []);

  const t = (tr, en, ru) => {
    if (currentLanguage === 'EN') return en;
    if (currentLanguage === 'RU') return ru || en || tr;
    return tr;
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    onSearch(value);
  };

  const toggleSearch = () => {
    setIsSearchExpanded(!isSearchExpanded);
    if (isSearchExpanded && searchTerm) {
      setSearchTerm('');
      onSearch('');
    }
  };

  return (
    <>
      {/* Mobile Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="px-4">
          {/* Main Header Row */}
          <div className="flex items-center justify-between h-14">
            {/* Logo - Mobile Optimized */}
            <div className="flex items-center">
              <h1 className="text-lg sm:text-xl font-bold text-primary-600 truncate">
                Samsun Keşfet
              </h1>
            </div>

            {/* Mobile Actions */}
            <div className="flex items-center space-x-2">
              {/* Search Toggle Button - Mobile */}
              <button
                onClick={toggleSearch}
                className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors md:hidden"
              >
                <Search className="w-5 h-5" />
              </button>

              {/* Language Selector - Mobile */}
              <div className="md:hidden">
                <LanguageSelector size="sm" labelMode="short" showIcon={false} onChange={setCurrentLanguage} />
              </div>

              {/* Desktop Search */}
              <div className="hidden md:flex items-center flex-1 max-w-md mx-4">
                <div className="relative w-full">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder={t('Ara...', 'Search...', 'Поиск...')}
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="block w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>

              {/* Desktop Language Selector */}
              <div className="hidden md:flex">
                <LanguageSelector labelMode="short" showIcon={false} onChange={setCurrentLanguage} />
              </div>

              {/* QR Scanner Button - Mobile Optimized */}
              <button
                onClick={onQRScan}
                className="flex items-center justify-center p-2 sm:px-3 sm:py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <QrCode className="w-5 h-5" />
                <span className="hidden sm:inline ml-2 text-sm font-medium">QR</span>
              </button>


            </div>
          </div>

          {/* Mobile Search Bar - Expandable */}
          {isSearchExpanded && (
            <div className="pb-3 md:hidden">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder={t('Konum, kategori ara...', 'Search location, category...', 'Искать место или категорию...')}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="block w-full pl-10 pr-3 py-3 text-base border border-gray-300 rounded-lg bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  autoFocus
                />
              </div>
            </div>
          )}
        </div>
      </header>
    </>
  );
};

export default Header;
